import React, { useEffect, useRef, useState } from 'react';
import { Box, Container } from '@mui/material';
import { useLocation, useNavigate } from 'react-router';
import LinkedTestCasesComponent from './linked-test-cases/LinkedTestCasesComponent';
import TestCycleDetails from './single-test-cycle/TestCycleDetails';
import TestCycleLoggedIssues from './TestCycleLoggedIssues';
import TestCycleHistory from '../../../features/testcycles/TestCycleHistory';
import { singleTestCyclePageTabs } from '../test-case/testCaseTableHead';
import Loader from '../../elements/Loader';
import Comments from '../../elements/common/comments/Comments';
import SecondaryHeader from '../../elements/SecondaryHeader';
import TestCycleReport from '../../elements/common/reports/TestCycleReport';
import Label from '../../../ui/primitives/Label';
import { fetchTestCycle } from '../../../modules/testcycles';
import { useNotification } from '../../../hooks/NotificationContext';
import { formatDate } from '../../../lib/utils';
import TestPlayerPageComponent from '../test-execution/TestPlayerPage';
import Error from '../../elements/Error';
import { Link } from 'react-router-dom';
import { useProjectUsers } from '../../../query/apiQueries';
import Attachments from '../../../features/attachments/Attachments';

const entityType = 'PluginTestCycle';

const useQuery = () => {
  return new URLSearchParams(useLocation().search);
};

const TestCycleViewPage = ({ testCycleKey, projectId }) => {
  const query = useQuery();
  const searchTerm = query.get('type');
  const [activeTab, setActiveTab] = useState(searchTerm || 'executions');
  const [data, setData] = useState(null);
  const { addNotification } = useNotification();
  const [error, setError] = useState(false);
  const location = useLocation();
  const [initialLoading, setInitialLoading] = useState(false);
  const initialEditMode = useRef(location.state?.isEdit || false);

  const navigate = useNavigate();

  const fetchTestCycleData = async (isInitial = false) => {
    if (isInitial) setInitialLoading(true);
    try {
      const response = await fetchTestCycle(testCycleKey, projectId);
      setData(response);
    } catch (error) {
      if (error?.message === 'NOT_FOUND') {
        setError(true);
      } else {
        const errorMessage =
          error?.message || 'Unable to fetch test cycle. Please try again...';
        addNotification('danger', errorMessage);
      }
    } finally {
      if (isInitial) setInitialLoading(false);
    }
  };

  const { data: users = [] } = useProjectUsers(projectId);

  useEffect(() => {
    if (!testCycleKey || !projectId) return;
    fetchTestCycleData(true);
  }, [testCycleKey, projectId]);

  useEffect(() => {
    if (location.state?.isEdit) {
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, []);

  if (error) {
    return (
      <Error>
        <h2>Test Cycle Not Found</h2>
        <p style={{ textAlign: 'center' }}>
          The test cycle you're looking for doesn't exist or may have been removed.{' '}
          <Link to={`/project/${projectId}/testcycles`}>Click here </Link>
          to view all the test cycles
        </p>
      </Error>
    );
  }

  return (
    <>
      {initialLoading || !data ? (
        <Loader />
      ) : (
        <Container maxWidth={false} disableGutters>
          <SecondaryHeader
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            pageTitle={
              <>
                {data?.name}
                <Box component="span" sx={{ ml: 1 }}>
                  <Label color={data?.status?.color} name={data?.status?.name} />
                </Box>
              </>
            }
            breadcrumbs={[
              {
                label: 'Testcycles',
                path: `/project/${projectId}/testcycles`,
                clickable: true,
              },
              {
                label: data?.key,
                path: `/project/${projectId}/testcycles/${testCycleKey}`,
                clickable: false,
              },
            ]}
            tabHeaders={singleTestCyclePageTabs}
            otherDetails={
              <>
                <Box
                  component="p"
                  sx={{
                    mb: 0,
                    pb: 0.25,
                    lineHeight: '20px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    fontSize: '1rem',
                  }}
                >
                  {data?.platforms && data?.platforms.length > 0 ? `${data?.platforms[0]}` : ''}{' '}
                  {data?.sub_platforms && data?.sub_platforms.length > 0
                    ? ` | ${data?.sub_platforms[0]}`
                    : ''}{' '}
                  {data?.browsers && data?.browsers.length > 0 ? `| ${data?.browsers[0]}` : ''}
                </Box>
                <Box component="p" sx={{ mb: 0 }}>
                  Created On: {formatDate(data?.created_at)}
                </Box>
              </>
            }
            showBackButton={true}
          />

          <Container maxWidth={false} disableGutters sx={{ px: 0 }}>
            {activeTab === 'details' && (
              <TestCycleDetails
                entityId={data?.id}
                data={data && data}
                fetchTestCycleData={fetchTestCycleData}
                initialEditMode={initialEditMode.current}
              />
            )}
            {activeTab === 'executions' && (
              <TestPlayerPageComponent
                testCycleId={data?.id}
                projectId={projectId}
                testCycleData={data && data}
              />
            )}
            {activeTab === 'testcases' && (
              <LinkedTestCasesComponent
                entityId={data?.id}
                users={users}
                data={data}
                fetchTestCycleData={fetchTestCycleData}
              />
            )}
            {activeTab === 'traceability' && (
              <TestCycleLoggedIssues entityId={data?.id} testcycle={data && data} />
            )}
            {activeTab === 'attachments' && (
              <Container maxWidth={false} disableGutters sx={{ px: 0 }}>
                <Attachments entityType={entityType} users={users} entityId={data?.id} />
              </Container>
            )}
            {activeTab === 'comments' && (
              <Container maxWidth={false} disableGutters sx={{ px: 0 }}>
                <Comments
                  entityType={entityType}
                  entityId={data?.id}
                  users={users}
                  entityKey={data?.key}
                />
              </Container>
            )}
            {activeTab === 'reports' && (
              <Container maxWidth={false} disableGutters sx={{ px: 0 }}>
                <TestCycleReport
                  testCycleId={data?.id}
                  projectId={projectId}
                  testCycleData={data}
                />
              </Container>
            )}
            {activeTab === 'history' && (
              <Container maxWidth={false} disableGutters sx={{ px: 0 }}>
                <TestCycleHistory
                  entityType={entityType}
                  entityId={data?.id}
                  users={users}
                />
              </Container>
            )}
          </Container>
        </Container>
      )}
    </>
  );
};

export default TestCycleViewPage;